import React, { useEffect, useState } from "react";
import { environment } from "environment/environment";
import "./Profile.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../common/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Profile = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [userPlanByUserId, setUserPlanByUserId] = useState(null);
  const [isPlanLoading, setIsPlanLoading] = useState(true);

  // ✅ Redirect to login if not logged in
  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  // ✅ Fetch user plan by userId
  useEffect(() => {
    let isCancelled = false;

    const fetchUserPlan = async (userId) => {
      if (!userId) {
        setIsPlanLoading(false);
        return;
      }

      setIsPlanLoading(true);

      try {
        const response = await fetch(
          `${environment.baseUrl}/user-pan/get-user-plain-by-user-id?userId=${userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );

        const res = await response.json();

        if (!isCancelled) {
          setUserPlanByUserId(res.status && res.data ? res.data : null);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching user plan:", error);
          toast.error("Error fetching user plan!");
          setUserPlanByUserId(null);
        }
      } finally {
        if (!isCancelled) setIsPlanLoading(false);
      }
    };

    if (user?.userId) {
      fetchUserPlan(user.userId);
    }

    return () => {
      isCancelled = true;
    };
  }, [user]);

  if (isLoading) return <main className="profile-loading">Loading your profile…</main>;
  if (!user) return null;

  const profileName = user.userName || "User";
  const firstName = profileName.trim().split(" ")[0] || "there";
  const profileInitial = profileName.trim().charAt(0).toUpperCase() || "U";
  const hasActivePlan = Boolean(userPlanByUserId);
  const role = user.role || user.post || (user.admin ? "Admin" : "Member");

  return (
    <main className="profile-container" aria-labelledby="profile-page-title">
      <div className="profile-shell">
        <section className="profile-hero" aria-labelledby="profile-page-title">
          <div className="profile-hero__content">
            <div className="profile-hero__identity">
              <span className="profile-hero__avatar" aria-hidden="true">{profileInitial}</span>
              <div>
                <span className="profile-eyebrow">ACCOUNT CENTER</span>
                <span className="profile-hero__identity-label">Your personal workspace</span>
              </div>
            </div>
            <h1 id="profile-page-title">Your profile, {firstName}</h1>
            <p>Review your account details and keep your membership information up to date.</p>
          </div>

          <div className={`profile-hero__access ${hasActivePlan ? "is-active" : "is-expired"}`}>
            <span className="profile-hero__access-label">
              <span className="profile-status-dot" aria-hidden="true" />
              {isPlanLoading ? "Checking access" : hasActivePlan ? "Access active" : "Subscription needed"}
            </span>
            <strong>
              {isPlanLoading
                ? "Checking membership"
                : hasActivePlan
                  ? `${userPlanByUserId.daysLeft ?? "--"} days remaining`
                  : "Renew to unlock tools"}
            </strong>
            <small>
              {hasActivePlan
                ? `${userPlanByUserId.planName || "Membership"} · ${userPlanByUserId.planValidity ?? "--"} days`
                : "Choose a plan to restore access"}
            </small>
          </div>
        </section>

        <section className="profile-section" aria-labelledby="profile-overview-title">
          <div className="profile-section__heading">
            <div>
              <span className="profile-eyebrow">YOUR ACCOUNT</span>
              <h2 id="profile-overview-title">Account overview</h2>
            </div>
            <span className="profile-section__caption">Your details, membership, and performance in one place</span>
          </div>

          <div className="profile-overview-grid">
            <article className="profile-card profile-card--account">
              <div className="profile-card__heading">
                <div className="profile-card__title-wrap">
                  <span className="profile-card__icon profile-card__icon--blue" aria-hidden="true">{profileInitial}</span>
                  <div>
                    <span className="profile-card__kicker">ACCOUNT</span>
                    <h3>Profile details</h3>
                  </div>
                </div>
              </div>

              <dl className="profile-detail-list">
                <div className="profile-detail-row"><dt>User ID</dt><dd>{user.userId || "--"}</dd></div>
                <div className="profile-detail-row"><dt>Name</dt><dd>{profileName}</dd></div>
                <div className="profile-detail-row"><dt>Mobile</dt><dd>{user.mobileNumber || "--"}</dd></div>
                <div className="profile-detail-row"><dt>Email</dt><dd>{user.email || "--"}</dd></div>
                <div className="profile-detail-row"><dt>Role</dt><dd>{role}</dd></div>
              </dl>
            </article>

            <article className={`profile-card profile-card--membership ${hasActivePlan ? "is-active" : "is-expired"}`}>
              <div className="profile-card__heading">
                <div className="profile-card__title-wrap">
                  <span className="profile-card__icon profile-card__icon--green" aria-hidden="true"><i className="fa fa-shield" /></span>
                  <div>
                    <span className="profile-card__kicker">MEMBERSHIP</span>
                    <h3>Plan &amp; validity</h3>
                  </div>
                </div>
                <span className={`profile-status-pill ${hasActivePlan ? "is-active" : "is-expired"}`}>
                  <span className="profile-status-dot" aria-hidden="true" />
                  {isPlanLoading ? "Checking" : hasActivePlan ? "Active" : "Expired"}
                </span>
              </div>

              {!isPlanLoading && !hasActivePlan && (
                <p className="profile-card__notice">Your previous plan has ended. Renew below to restore access to paid tools.</p>
              )}

              <dl className="profile-detail-list">
                <div className="profile-detail-row"><dt>Plan</dt><dd>{userPlanByUserId?.planName || "No active plan"}</dd></div>
                <div className="profile-detail-row"><dt>Amount</dt><dd>{hasActivePlan ? `₹${userPlanByUserId.planAmount ?? "--"}` : "--"}</dd></div>
                <div className="profile-detail-row"><dt>Validity</dt><dd>{hasActivePlan ? `${userPlanByUserId.planValidity ?? "--"} days` : "--"}</dd></div>
                <div className="profile-detail-row"><dt>Days left</dt><dd className={hasActivePlan ? "profile-value--green" : "profile-value--muted"}>{userPlanByUserId?.daysLeft ?? "--"}</dd></div>
                <div className="profile-detail-row"><dt>Start</dt><dd>{userPlanByUserId?.subscribeDateStart || "--"}</dd></div>
                <div className="profile-detail-row"><dt>Ends</dt><dd>{userPlanByUserId?.subscribeDateEnd || "--"}</dd></div>
              </dl>

              <button type="button" className="profile-card__action" onClick={() => navigate("/subscribe")}>
                {hasActivePlan ? "Manage membership" : "View membership plans"}
                <i className="fa fa-arrow-right" aria-hidden="true" />
              </button>
            </article>

            <article className="profile-card profile-card--performance">
              <div className="profile-card__heading">
                <div className="profile-card__title-wrap">
                  <span className="profile-card__icon profile-card__icon--purple" aria-hidden="true"><i className="fa fa-line-chart" /></span>
                  <div>
                    <span className="profile-card__kicker">SNAPSHOT</span>
                    <h3>Performance</h3>
                  </div>
                </div>
              </div>

              <div className="profile-performance-metrics">
                <div className="profile-performance-metric"><span>Total trades</span><strong>248</strong></div>
                <div className="profile-performance-metric"><span>Win rate</span><strong>78%</strong></div>
                <div className="profile-performance-metric profile-performance-metric--positive"><span>Profit</span><strong>₹1,24,500</strong></div>
              </div>
            </article>
          </div>
        </section>
      </div>

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} />
    </main>
  );
};

export default Profile;
